class TestimonialCards extends HTMLElement {
  static CARD_POSES = [
    { tx: -5, ty: 0, rz: -15, scale: 0.9 },
    { tx: -63, ty: -10, rz: -4, scale: 0.9 },
    { tx: -110, ty: -18, rz: -2, scale: 0.9 },
    { tx: 150, ty: -117, rz: 7, scale: 0.9 },
    { tx: 80, ty: -110, rz: 8, scale: 0.9 },
    { tx: 9, ty: -100, rz: 18, scale: 0.9 },
  ];

  static STAGGER = 0.11;
  static SETTLE_DURATION = 0.42;
  static MAX_ANIMATED_CARDS = 6;

  constructor() {
    super();
    this.cards = [];
    this.poses = [];
    this.scatterScale = 1;
    this.loopActive = false;
    this.lastSectionProgress = 0;
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.desktopMedia = window.matchMedia('(min-width: 1025px)');
    this.onResize = this.onResize.bind(this);
    this.onBreakpointChange = this.onBreakpointChange.bind(this);
    this.tick = this.tick.bind(this);
  }

  isScrollAnimationActive() {
    return !this.reduceMotion && this.desktopMedia.matches;
  }

  connectedCallback() {
    this.section = this.closest('.testimonial-cards');
    this.cards = Array.from(this.querySelectorAll('.testimonial-card'));
    if (!this.cards.length) return;

    document.documentElement.classList.add('js');
    this.updateCardHeightVariable();

    if (this.isScrollAnimationActive()) {
      this.section?.classList.add('testimonial-cards--scroll-driven');
      this.resetExtraCardsToStatic();
      this.refreshLayoutCache();
      this.lastSectionProgress = this.getSectionProgress();
      this.applyTransforms(this.lastSectionProgress);
      this.startLoop();
    } else {
      this.resetAllCardsToStatic();
    }

    this.desktopMedia.addEventListener('change', this.onBreakpointChange);
    window.addEventListener('resize', this.onResize, { passive: true });
  }

  disconnectedCallback() {
    this.stopLoop();
    this.desktopMedia.removeEventListener('change', this.onBreakpointChange);
    window.removeEventListener('resize', this.onResize);
    if (this._resizeTimer != null) {
      window.clearTimeout(this._resizeTimer);
      this._resizeTimer = null;
    }
    this.section?.classList.remove('testimonial-cards--scroll-driven');
    this.section?.style.removeProperty('--cardHeight');
  }

  updateCardHeightVariable() {
    const root = this.section || this;
    if (!this.cards.length) return;

    root.style.removeProperty('--cardHeight');

    let maxHeight = 0;
    this.cards.forEach((card) => {
      const h = card.offsetHeight;
      if (h > maxHeight) maxHeight = h;
    });

    if (maxHeight > 0) {
      root.style.setProperty('--cardHeight', `${maxHeight}px`);
    }
  }

  isAnimatedIndex(index) {
    return index < TestimonialCards.MAX_ANIMATED_CARDS;
  }

  resetAllCardsToStatic() {
    this._extraFadeStart = null;
    this._extraFadeDir = 0;
    this._extraOpacity = 0;
    this._extraFadeFrom = 0;
    this.section?.classList.remove('testimonial-cards--scroll-driven');
    this.cards.forEach((card) => {
      card.style.transform = '';
      card.style.opacity = '';
    });
  }

  resetExtraCardsToStatic() {
    this.cards.forEach((card, index) => {
      if (!this.isAnimatedIndex(index)) {
        card.style.transform = '';
        card.style.opacity = '0';
      }
    });
  }

  static clamp(n, min, max) {
    return Math.min(max, Math.max(min, n));
  }

  static lerp(a, b, t) {
    return a + (b - a) * t;
  }

  static smoothstep(t) {
    const x = TestimonialCards.clamp(t, 0, 1);
    return x * x * (3 - 2 * x);
  }

  static columnScatterFactor() {
    const w = window.innerWidth;
    if (w >= 1025) return 1;
    if (w >= 768) return 0.68;
    return 0.38;
  }
  getReferenceWidth() {
    const root = this.section || this;
    const fromCss = parseFloat(getComputedStyle(root).getPropertyValue('--tc-ref-viewport-width'));
    if (Number.isFinite(fromCss) && fromCss > 0) return fromCss;
    return window.innerWidth;
  }

  /** Live content width used to scale scatter vs reference. */
  getLayoutWidth() {
    const container = this.querySelector('.testimonial-cards-container');
    const grid = this.querySelector('.testimonial-cards-grid');
    const width = container?.clientWidth || grid?.clientWidth || 0;
    return width > 0 ? width : window.innerWidth;
  }

  viewportScatterScale() {
    const ref = this.getReferenceWidth();
    const current = this.getLayoutWidth();
    const widthScale = TestimonialCards.clamp(current / ref, 0.35, 1.12);
    return widthScale * TestimonialCards.columnScatterFactor();
  }

  /**
   * Bottom-row cards (indexes 3–5) use a strong upward ty.
   * Width scaling shrinks that lift on narrower desktops and leaves them
   * below the fold. Keep at least the tuned lift, and pull them up further
   * when the viewport is shorter than the 900px design height.
   */
  verticalPoseScale(index) {
    const widthScale = this.scatterScale;
    if (index < 3) return widthScale;

    const shortScreenLift = TestimonialCards.clamp(900 / Math.max(window.innerHeight, 1), 1, 1.35);
    return Math.max(widthScale, shortScreenLift);
  }

  buildPose(card, index) {
    const base = TestimonialCards.CARD_POSES[index];
    const s = this.scatterScale;
    const tyScale = this.verticalPoseScale(index);
    const w = card.offsetWidth;
    const h = card.offsetHeight;
    return {
      txPx: (base.tx / 100) * w * s,
      tyPx: (base.ty / 100) * h * tyScale,
      rz: base.rz * s,
      scale: base.scale,
    };
  }

  refreshLayoutCache() {
    this.updateCardHeightVariable();
    this.scatterScale = this.viewportScatterScale();
    this.poses = this.cards.map((card, index) => {
      if (!this.isAnimatedIndex(index)) return null;
      return this.buildPose(card, index);
    });
    this.clampPosesInsideSection();
  }

  /**
   * Shopify spacing comes from padding-block-start / --topSpacer, so card
   * poses are pulled back inside the section instead of changing padding.
   */
  clampPosesInsideSection() {
    const section = this.section;
    if (!section) return;

    const prevTransforms = this.cards.map((card) => card.style.transform);
    this.cards.forEach((card, index) => {
      if (this.isAnimatedIndex(index)) card.style.transform = 'none';
    });

    const sectionRect = section.getBoundingClientRect();
    const gap = 8;

    this.cards.forEach((card, index) => {
      if (!this.isAnimatedIndex(index)) return;
      const pose = this.poses[index];
      if (!pose) return;

      const rect = card.getBoundingClientRect();
      const w = card.offsetWidth;
      const h = card.offsetHeight;
      const rad = (Math.abs(pose.rz) * Math.PI) / 180;
      const half =
        ((Math.abs(Math.sin(rad)) * w + Math.abs(Math.cos(rad)) * h) * pose.scale) / 2;
      const layoutCenter = rect.top + h / 2;

      let centerY = layoutCenter + pose.tyPx;
      const topLimit = sectionRect.top + gap;
      const bottomLimit = sectionRect.bottom - gap;

      if (centerY - half < topLimit) {
        pose.tyPx += topLimit - (centerY - half);
        centerY = layoutCenter + pose.tyPx;
      }
      if (centerY + half > bottomLimit) {
        pose.tyPx -= centerY + half - bottomLimit;
      }
    });

    this.cards.forEach((card, index) => {
      card.style.transform = prevTransforms[index];
    });
  }

  getProgressAnchors() {
    const root = this.section || this;
    const styles = getComputedStyle(root);
    const startVp = parseFloat(styles.getPropertyValue('--tc-progress-start')) || 0.92;
    const endVp = parseFloat(styles.getPropertyValue('--tc-progress-end')) || 0.28;
    return { startVp, endVp };
  }
  getSectionProgress() {
    const vh = window.innerHeight;
    const { startVp, endVp } = this.getProgressAnchors();
    const target =
      this.querySelector('[data-tc-progress-target]') ||
      this.querySelector('.testimonial-cards-grid') ||
      this.section ||
      this;
    const rect = target.getBoundingClientRect();
    const start = vh * startVp;
    const end = vh * endVp;
    if (rect.top >= start) return 0;
    if (rect.top <= end) return 1;
    return (start - rect.top) / (start - end);
  }
  getCardProgress(sectionProgress, index) {
    const maxIdx = TestimonialCards.MAX_ANIMATED_CARDS - 1;
    const stagger = TestimonialCards.STAGGER;
    const duration = TestimonialCards.SETTLE_DURATION;
    const totalSpan = duration + maxIdx * stagger;
    const raw = TestimonialCards.clamp(sectionProgress, 0, 1) / totalSpan;
    return TestimonialCards.smoothstep(raw);
  }

  /**
   * Cards after the first 6 fade to 1 only after every animated card has settled.
   * Reversing the scroll fades them back to 0 over the same duration.
   */
  getExtraCardOpacity(sectionProgress) {
    const settled = this.getCardProgress(sectionProgress, 0) >= 0.995;
    const now = performance.now();
    const fadeMs = 800;
    if (this._extraOpacity == null) this._extraOpacity = 0;

    const wantIn = settled;
    const fadingIn = this._extraFadeDir === 1;
    const fadingOut = this._extraFadeDir === -1;

    if (wantIn && !fadingIn) {
      this._extraFadeDir = 1;
      this._extraFadeStart = now;
      this._extraFadeFrom = this._extraOpacity;
    } else if (!wantIn && this._extraOpacity > 0 && !fadingOut) {
      this._extraFadeDir = -1;
      this._extraFadeStart = now;
      this._extraFadeFrom = this._extraOpacity;
    } else if (!wantIn && this._extraOpacity <= 0) {
      this._extraFadeDir = 0;
      this._extraOpacity = 0;
      return 0;
    }

    const elapsed = now - this._extraFadeStart;
    const t = TestimonialCards.smoothstep(TestimonialCards.clamp(elapsed / fadeMs, 0, 1));
    const target = this._extraFadeDir === 1 ? 1 : 0;
    this._extraOpacity = TestimonialCards.lerp(this._extraFadeFrom, target, t);
    return this._extraOpacity;
  }

  buildTransformString(pose, settleProgress) {
    const t = TestimonialCards.clamp(settleProgress, 0, 1);
    const tx = TestimonialCards.lerp(pose.txPx, 0, t);
    const ty = TestimonialCards.lerp(pose.tyPx, 0, t);
    const rz = TestimonialCards.lerp(pose.rz, 0, t);
    const scale = TestimonialCards.lerp(pose.scale, 1, t);
    return `translate3d(${tx.toFixed(2)}px, ${ty.toFixed(2)}px, 0) scale3d(${scale.toFixed(4)}, ${scale.toFixed(4)}, 1) rotateZ(${rz.toFixed(2)}deg)`;
  }

  applyTransforms(sectionProgress) {
    const p = sectionProgress ?? this.getSectionProgress();
    this.cards.forEach((card, index) => {
      if (!this.isAnimatedIndex(index)) {
        card.style.transform = '';
        card.style.opacity = this.getExtraCardOpacity(p).toFixed(3);
        return;
      }
      const pose = this.poses[index];
      if (!pose) return;
      const cardProgress = this.getCardProgress(p, index);
      card.style.transform = this.buildTransformString(pose, cardProgress);
    });
  }

  update() {
    const p = this.getSectionProgress();
    this.lastSectionProgress = p;
    this.applyTransforms(p);
  }

  startLoop() {
    if (this.loopActive) return;
    this.loopActive = true;
    this._rafId = requestAnimationFrame(this.tick);
  }

  stopLoop() {
    this.loopActive = false;
    if (this._rafId != null) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
  }

  tick() {
    if (!this.loopActive) return;
    this.update();
    this._rafId = requestAnimationFrame(this.tick);
  }

  onBreakpointChange() {
    this.cards = Array.from(this.querySelectorAll('.testimonial-card'));
    this.updateCardHeightVariable();
    if (!this.isScrollAnimationActive()) {
      this.stopLoop();
      this.resetAllCardsToStatic();
      return;
    }
    this.section?.classList.add('testimonial-cards--scroll-driven');
    this.resetExtraCardsToStatic();
    this.refreshLayoutCache();
    this.lastSectionProgress = this.getSectionProgress();
    this.applyTransforms(this.lastSectionProgress);
    this.startLoop();
  }

  onResize() {
    window.clearTimeout(this._resizeTimer);
    this._resizeTimer = window.setTimeout(() => {
      this.cards = Array.from(this.querySelectorAll('.testimonial-card'));
      this.updateCardHeightVariable();
      if (!this.isScrollAnimationActive()) {
        this.stopLoop();
        this.resetAllCardsToStatic();
        return;
      }
      this.section?.classList.add('testimonial-cards--scroll-driven');
      this.resetExtraCardsToStatic();
      this.refreshLayoutCache();
      this.applyTransforms(this.getSectionProgress());
    }, 120);
  }
}

if (!customElements.get('testimonial-cards')) {
  customElements.define('testimonial-cards', TestimonialCards);
}
